import { SQLiteDBConnection } from '@capacitor-community/sqlite'
import { Ref, watch, toValue } from 'vue'
import { KeyValueStorer } from '../KeyValueStorer'
import { dbStore } from './dbStore'
import { sqlite } from './sqliteConnection'
import { Capacitor } from '@capacitor/core'
import { forgetSqliteKv, readSqliteKv, rememberSqliteKvWrite, sqliteKvSlot } from './sqliteKvCache'
import { sqliteCellValue } from './sqliteCellValue'
import {
  establishSqliteConnection,
  type SqliteConnectRuntime,
} from './establishSqliteConnection'

const createDbPromises: Record<string, Promise<any>> = {}
const saveTimeouts: Record<string, NodeJS.Timeout> = {}
const SAVE_DEBOUNCE_MS = 200
const platform = Capacitor.getPlatform()

function defaultSqliteConnectRuntime (): SqliteConnectRuntime<SQLiteDBConnection> {
  return {
    checkConnectionsConsistency: () => sqlite.checkConnectionsConsistency(),
    isConnection: (dbName, readonly) => sqlite.isConnection(dbName, readonly),
    retrieveConnection: (dbName, readonly) => sqlite.retrieveConnection(dbName, readonly),
    createConnection: (dbName, encrypted, mode, version, readonly) =>
      sqlite.createConnection(dbName, encrypted, mode, version, readonly),
    open: db => db.open(),
    isDBOpen: db => db.isDBOpen(),
    createKeyValueTable: db => db.execute('CREATE TABLE IF NOT EXISTS KeyValue (key, value)'),
  }
}

async function connect (dbKey: string) {
  try {
    return await establishSqliteConnection(toValue(dbKey), dbStore, defaultSqliteConnectRuntime())
  } catch (e) {
    console.error(e)
    throw new Error('error creating and opening connection')
  }
}

export async function getDb (dbKey = 'fuellox'): Promise<SQLiteDBConnection> {
  const key = toValue(dbKey)
  if (!createDbPromises[key]) {
    createDbPromises[key] = (dbStore[key] ? Promise.resolve(dbStore[key]) : connect(key))
      .catch((error: unknown) => {
        delete createDbPromises[key]
        throw error
      })
  }

  await createDbPromises[key]

  const db = dbStore[key]
  if (!db) {
    delete createDbPromises[key]
    throw new Error('error creating and opening connection')
  }

  return db
}

const updateQuery = 'UPDATE KeyValue SET value = ? WHERE key = ?'
const createQuery = 'INSERT INTO KeyValue (key, value) VALUES (?,?)'
const findQuery = 'SELECT value FROM KeyValue WHERE key = ?'
const removeQuery = 'DELETE FROM KeyValue WHERE key = ?'

export async function executeSql (dbKey: string, sql: string, params: any[]) {
  dbStore[toValue(dbKey)] = await getDb(toValue(dbKey))

  const result = await dbStore[toValue(dbKey)].query(sql, params)
  return sqliteCellValue(result.values?.[0])
}

export function forgetSqliteConnection (dbKey: string): void {
  if (saveTimeouts[dbKey]) {
    clearTimeout(saveTimeouts[dbKey])
    delete saveTimeouts[dbKey]
  }
  forgetSqliteKv(toValue(dbKey))
  delete dbStore[toValue(dbKey)]
  delete createDbPromises[toValue(dbKey)]
}

export function sqliteHydrationIsStale<T> (snapshot: T | undefined, current: T | undefined): boolean {
  return current !== snapshot
}

export async function notifyInitialGet (
  getPromise: Promise<unknown>,
  onInitialGet?: (value: unknown) => void,
): Promise<void> {
  try {
    const value = await getPromise
    onInitialGet?.(value)
  } catch {
    onInitialGet?.(undefined)
  }
}

async function saveSqliteToStore (dbKey: string): Promise<void> {
  try {
    const dbName = toValue(dbKey) + '.db'

    const hasConnection = await sqlite.isConnection(dbName, false)
    if (!hasConnection.result) {
      return
    }

    const db = dbStore[toValue(dbKey)]
    if (!db) {
      return
    }

    const isOpen = await db.isDBOpen()
    if (!isOpen.result) {
      return
    }

    try {
      // Close the connection - this automatically saves to store on web
      await db.close()
      await db.open()
    } catch (error: any) {
      console.error(`Failed to close/reopen ${dbName}:`, error)
      try {
        await db.open()
      } catch (reopenError) {
        console.error('Failed to reopen connection:', reopenError)
      }
      throw error
    }
  } catch (error: any) {
    if (!error.message?.includes('No available connection')) {
      console.error('Error auto-saving to store:', error)
    }
  }
}

function debouncedSaveToStore (dbKey: string) {
  if (saveTimeouts[dbKey]) {
    clearTimeout(saveTimeouts[dbKey])
  }

  saveTimeouts[dbKey] = setTimeout(() => {
    delete saveTimeouts[dbKey]
    void saveSqliteToStore(dbKey)
  }, SAVE_DEBOUNCE_MS)
}

export async function flushSqlitePersistence (dbKey: string): Promise<void> {
  if (saveTimeouts[dbKey]) {
    clearTimeout(saveTimeouts[dbKey])
    delete saveTimeouts[dbKey]
  }
  if (platform !== 'web') {
    return
  }
  await saveSqliteToStore(dbKey)
}

export default function makeSqliteStorer<T = unknown> (
  dbKey: string,
  key: string,
  syncedRef?: Ref<T>,
  serializer?: { read: (value: any) => any, write: (value: any) => any },
  options?: { onInitialGet: (value: any) => void },
): KeyValueStorer {
  async function remove () {
    const result = await executeSql(toValue(dbKey), removeQuery, [key])

    return result
  }

  async function get (options: { skipRefUpdate: boolean } = {}) {
    const snapshot = syncedRef ? syncedRef.value : undefined
    const slot = sqliteKvSlot(toValue(dbKey), key)
    const raw = await readSqliteKv(slot, async () => {
      return await executeSql(toValue(dbKey), findQuery, [key])
    })

    if (options.skipRefUpdate) {
      return raw
    }
    if (syncedRef && sqliteHydrationIsStale(snapshot, syncedRef.value)) {
      return serializer ? serializer.read(raw) : raw
    }
    if (serializer && syncedRef) {
      syncedRef.value = serializer.read(raw)
    } else if (syncedRef) {
      syncedRef.value = raw
    }

    return serializer ? serializer.read(raw) : raw
  }

  async function set (value: string, options: { skipRefUpdate: boolean } = {}) {
    const serializedValue = serializer ? serializer.write(value) : value

    const currentValue = await get({ skipRefUpdate: true })
    const currentValueIsBadData = [
      'null', 'undefined', 'Undefined', '{}', '[]', '', undefined,
    ].includes(currentValue)

    if (currentValueIsBadData) {
      await remove()
    }

    if (!currentValueIsBadData) {
      await executeSql(toValue(dbKey), updateQuery, [serializedValue, key])
    } else {
      await executeSql(toValue(dbKey), createQuery, [key, serializedValue])
    }

    rememberSqliteKvWrite(sqliteKvSlot(toValue(dbKey), key), serializedValue)

    if (platform === 'web') {
      debouncedSaveToStore(dbKey)
    }

    if (!syncedRef || options.skipRefUpdate) {
      return
    }
    if (serializer) {
      syncedRef.value = serializedValue
    } else {
      syncedRef.value = serializedValue
    }
  }

  if (syncedRef) {
    watch(syncedRef, (newRef, oldRef) => {
      set(serializer?.write(newRef), { skipRefUpdate: true })
    })
  }

  void notifyInitialGet(get(), options?.onInitialGet)

  return {
    get,
    set,
  }
}
